/**
 * 获取url参数
 */
export const getUrlParam = paramName => {
  // 优先使用现代API
  if (window.URLSearchParams) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(paramName);
  }

  // 降级方案：正则表达式
  const url = window.location.href;
  const regex = new RegExp('[?&]' + paramName + '=([^&]*)');
  const match = url.match(regex);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * 获取url的全部参数
 */
export const getUrlParams = () => {
  // 优先使用现代API
  if (window.URLSearchParams && Object.fromEntries) {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return Object.fromEntries(urlParams);
    } catch (error) {
      // 如果 Object.fromEntries 不支持，降级到手动转换
      console.warn('Object.fromEntries not supported, using fallback:', error);
    }
  }

  // 降级方案：正则表达式
  const url = window.location.href;
  const params = {};

  // 匹配所有查询参数
  const regex = /[?&]([^=]+)=([^&]*)/g;
  let match;

  while ((match = regex.exec(url)) !== null) {
    const key = decodeURIComponent(match[1]);
    const value = decodeURIComponent(match[2]);
    params[key] = value;
  }

  return params;
};

/**
 * 获取 query 中的单个参数值
 */
export const getQueryStringValue = value => {
  if (Array.isArray(value)) {
    return value[0] ?? '';
  }
  return value ?? '';
};

/**
 * 从 query 中提取跳转参数
 */
export const getJumpParamsFromQuery = query => {
  return Object.entries(query).reduce((result, [key, value]) => {
    if (key === 'action') return result;

    const nextValue = getQueryStringValue(value);
    if (nextValue === '' || nextValue === null || nextValue === undefined) {
      return result;
    }

    result[key] = nextValue;
    return result;
  }, {});
};

/**
 * 追加 url 参数
 * @param {String} rawUrl 原始 url
 * @param {Object} params 需要追加的参数
 * @returns {{ url: String, search: String }} 拼接结果
 */
export const appendQueryParams = (rawUrl, params = {}) => {
  if (!rawUrl) {
    return { url: rawUrl, search: '' };
  }

  const urlObj = new URL(rawUrl, window.location.origin);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    urlObj.searchParams.set(key, String(value));
  });

  return {
    url: urlObj.toString(),
    search: urlObj.searchParams.toString(),
  };
};

/**
 * 修复IOS滚动问题
 */
export const fixIOSScroll = () => {
  const inputs = document.querySelectorAll('input, textarea');
  inputs.forEach(input => {
    input.addEventListener('focus', () => {
      setTimeout(() => {
        input.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 300);
    });
  });
};

/**
 * 是否为 Google Chrome（排除 Edge、Opera、三星/华为/小米等内置浏览器）
 */
export const isChromeBrowser = () => {
  const ua = navigator.userAgent || '';
  const isChromium = ua.includes('Chrome') || ua.includes('CriOS');
  if (!isChromium) return false;

  // Chromium 系浏览器 UA 均含 Chrome，需排除常见非 Chrome 内核壳浏览器
  return !/(EdgA?|OPR\/|SamsungBrowser|UCBrowser|HuaweiBrowser|HbBrowser|MiuiBrowser|VivoBrowser|HeyTapBrowser|QQBrowser|Quark)/i.test(ua);
};

/**
 * 获取文件可靠大小（部分安卓浏览器 file.size 可能为 0）
 */
export async function getReliableFileSize(file) {
  if (file?.size > 0) return file.size;
  try {
    const buf = await file.arrayBuffer();
    return buf.byteLength;
  } catch {
    return 0;
  }
}

/**
 * 格式化上传文件大小展示
 */
export function formatUploadSize(bytes, compressedType) {
  if (!bytes || bytes <= 0) return '';

  let text = '上传';
  if (compressedType === 1) {
    text = '压缩上传';
  } else if (compressedType === 2) {
    text = '压缩上传 ';
  }

  if (bytes < 1024 * 1024) {
    return `${text}${Math.max(1, Math.round(bytes / 1024))}KB`;
  }
  return `${text}${(bytes / 1024 / 1024).toFixed(2)}MB`;
}

/**
 * 压缩图片
 */
export function compressImage(file, options = { maxWidth: 1080, maxHeight: 1920, quality: 0.75 }) {
  const { maxWidth, maxHeight, quality } = options;

  return new Promise((resolve, reject) => {
    const fileType = file?.type || '';
    // 部分安卓相册 file.type 为空，交给 FileReader + Image 解码校验；已知非图片类型直接拒绝
    if (!file || (fileType && !fileType.startsWith('image/'))) {
      return reject(new Error('请选择有效的图片文件'));
    }

    const reader = new FileReader();
    const img = new Image();

    reader.readAsDataURL(file);

    reader.onload = e => {
      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error('图片读取失败'));
    };

    img.onload = () => {
      const originWidth = img.width;
      const originHeight = img.height;

      let targetWidth = originWidth;
      let targetHeight = originHeight;

      if (originWidth > maxWidth || originHeight > maxHeight) {
        if (originWidth / originHeight > maxWidth / maxHeight) {
          targetWidth = maxWidth;
          targetHeight = Math.round(maxWidth * (originHeight / originWidth));
        } else {
          targetHeight = maxHeight;
          targetWidth = Math.round(maxHeight * (originWidth / originHeight));
        }
      }

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      context.clearRect(0, 0, targetWidth, targetHeight);
      context.drawImage(img, 0, 0, targetWidth, targetHeight);

      const base64 = canvas.toDataURL('image/jpeg', quality);

      canvas.toBlob(
        blob => {
          if (!blob) {
            return reject(new Error('图片压缩失败'));
          }
          resolve({ base64, file: blob });
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      reject(new Error('图片加载失败'));
    };
  });
}

/**
 * 获取设备类型
 */
export const getDeviceType = () => {
  try {
    // 获取设备类型
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    // 更安全的 iOS 检测，避免 navigator.maxTouchPoints 不存在的情况
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && typeof navigator.maxTouchPoints !== 'undefined' && navigator.maxTouchPoints > 1);

    if (isIOS) {
      return 'iOS';
    }

    // 判断是否为移动设备（手机端）
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 1 && /Macintosh/.test(ua));

    // 对于手机端，只要不是 iOS，就算是 Android
    if (isMobile) {
      return 'Android';
    }

    return 'PC';
  } catch (e) {
    console.warn('Device type detection failed:', e);

    // 默认返回 Android
    return 'Android';
  }
};

/**
 * iOS 15 / 16 系统 Safari（含 WebView 常见 UA），用于处理外跳 loading 与历史支付宝页焦点错乱
 */
export const isIosSafari15Or16 = () => {
  if (getDeviceType() !== 'iOS') return false;
  const ua = navigator.userAgent || '';
  const m = ua.match(/(?:CPU (?:iPhone |iPad )?OS|CPU OS|iPhone OS) (\d+)[_/]/);
  if (!m) return false;
  const major = parseInt(m[1], 10);
  return major === 15 || major === 16;
};

/**
 * 乘法计算，避免精度问题， 第一个参数可能有小数的情况, 第二个参数必须是整数
 */
export const multiplyOnlyFloat = (floatNum, intNum) => {
  const s1 = floatNum.toString();

  // 如果s1没有小数直接返回
  if (!s1.includes('.')) {
    return floatNum * intNum;
  }

  // 获取s1的小数位数
  const decimalPlaces = s1.split('.')[1].length;

  // 移除小数点的数，相乘后，再除以10的小数位数次方
  return (Number(s1.replace('.', '')) * intNum) / Math.pow(10, decimalPlaces);
};

/**
 * 两个数的精确乘法（支持任意两个整数或者浮点数，不丢失精度）
 * - 原理：将两数的小数点移除，先按整数相乘，再按小数位数还原
 * - 说明：在金额等常规范围（不超过 2^53-1）下，整数相乘不会溢出
 */
export const multiplyAllFloat = (num1, num2) => {
  // 兜底校验
  if (num1 == null || num2 == null) return NaN;

  // 处理符号
  const s1 = String(num1).trim();
  const s2 = String(num2).trim();
  const sign = (s1.startsWith('-') ? -1 : 1) * (s2.startsWith('-') ? -1 : 1);

  // 去除符号
  const a1 = s1.replace('-', '');
  const a2 = s2.replace('-', '');

  // 小数位数
  const dp1 = a1.includes('.') ? a1.split('.')[1].length : 0;
  const dp2 = a2.includes('.') ? a2.split('.')[1].length : 0;

  // 去掉小数点后的整数值
  const i1 = Number(a1.replace('.', ''));
  const i2 = Number(a2.replace('.', ''));

  // 整数相乘后再除以 10^(dp1+dp2)
  const product = (i1 * i2) / Math.pow(10, dp1 + dp2);
  return sign < 0 ? -product : product;
};

/**
 * 两个数的精确除法（支持整数/浮点数/混合，不丢失精度）
 * - 原理：移除小数点后转为整数做除法，再按小数位差恢复数值
 * - 说明：在金额等常规范围（不超过 2^53-1）内有效；分母为 0 返回 NaN
 */
export const divideAllFloat = (num1, num2) => {
  // 兜底校验
  if (num1 == null || num2 == null) return NaN;

  // 处理符号
  const s1 = String(num1).trim();
  const s2 = String(num2).trim();
  const sign = (s1.startsWith('-') ? -1 : 1) * (s2.startsWith('-') ? -1 : 1);

  // 去除符号
  const a1 = s1.replace('-', '');
  const a2 = s2.replace('-', '');

  // 小数位数
  const dp1 = a1.includes('.') ? a1.split('.')[1].length : 0;
  const dp2 = a2.includes('.') ? a2.split('.')[1].length : 0;

  // 去掉小数点后的整数值
  const i1 = Number(a1.replace('.', ''));
  const i2 = Number(a2.replace('.', ''));

  // 分母为 0
  if (i2 === 0) return NaN;

  // 整数相除后再乘以 10^(dp2-dp1)
  const quotient = (i1 / i2) * Math.pow(10, dp2 - dp1);
  return sign < 0 ? -quotient : quotient;
};

/**
 * 两个数的精确减法（支持整数/浮点数/混合，不丢失精度）
 * - 原理：将两数按最大的小数位数对齐到整数尺度后相减，再缩放回去
 * - 输入支持 number 或 string；null/undefined/空字符串按 0 处理
 */
export const subtractAllFloat = (num1, num2) => {
  const normalize = v => {
    if (v === '' || v === null || v === undefined) return { ok: true, sign: 1, abs: '0', dp: 0 };

    let s = String(v).trim();
    if (s === '') return { ok: true, sign: 1, abs: '0', dp: 0 };

    // 若为非标准数字字符串，尝试转成 number 再规范化（兜底）
    if (!/^[-+]?\d+(?:\.\d+)?$/.test(s)) {
      const n = Number(v);
      if (!Number.isFinite(n)) return { ok: false };
      s = String(n);
      if (!/^[-+]?\d+(?:\.\d+)?$/.test(s)) return { ok: false };
    }

    const sign = s.startsWith('-') ? -1 : 1;
    const abs = s.replace(/^[-+]/, '');
    const dp = abs.includes('.') ? abs.split('.')[1].length : 0;
    return { ok: true, sign, abs, dp };
  };

  const a = normalize(num1);
  const b = normalize(num2);
  if (!a.ok || !b.ok) return NaN;

  const maxDp = Math.max(a.dp, b.dp);
  const factor = Math.pow(10, maxDp);

  const toAlignedInt = ({ sign, abs, dp }) => {
    const digits = Number(abs.replace('.', '')) || 0;
    const scaled = digits * Math.pow(10, maxDp - dp);
    return sign < 0 ? -scaled : scaled;
  };

  const result = (toAlignedInt(a) - toAlignedInt(b)) / factor;
  return Object.is(result, -0) ? 0 : result;
};

/**
 * 截断小数位到指定长度（不四舍五入）
 * - 如果实际小数位数超过 decimalPlaces，则直接截掉多余位
 * - 如果不足或刚好，则保持原值
 */
export const floatNumCut = (num, decimalPlaces = 2) => {
  if (!Number.isFinite(num)) return NaN;
  const dp = Math.max(0, Number(decimalPlaces) || 0);
  if (dp === 0) return Math.trunc(num);

  const factor = Math.pow(10, dp);
  // 先精确放大到整数尺度，再做向零截断，最后缩回
  const scaled = multiplyOnlyFloat(num, factor);
  const truncated = Math.trunc(scaled);
  return truncated / factor;
};

/**
 * 显示格式：至少保留两位小数，超过两位小数时截取到两位（不四舍五入）
 * - 示例：0 => "0.00"；0.1 => "0.10"；0.11 => "0.11"；0.123 => "0.12"；0.999 => "0.99"
 * - 输入支持 number 或 string；非法输入返回 "0.00"
 */
export const formatMinTwoDecimals = value => {
  if (value === null || value === undefined) return '0.00';
  let s = String(value).trim();
  // 尝试规范化数字字符串
  if (!/^[-+]?\d+(?:\.\d+)?$/.test(s)) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0.00';
    s = String(n);
  }

  // 先转换为数字，使用 floatNumCut 截取到两位小数（不四舍五入）
  const num = Number(s);
  if (!Number.isFinite(num)) return '0.00';
  const truncated = floatNumCut(num, 2);

  // 格式化为至少两位小数的字符串
  s = String(truncated);
  const sign = s.startsWith('-') ? '-' : '';
  if (sign) s = s.slice(1);
  let [intPart, decPart = ''] = s.split('.');
  // 去除整数部分前导零（保留单个 0）
  intPart = intPart.replace(/^0+(\d)/, '$1');
  if (intPart === '') intPart = '0';
  if (decPart.length === 0) decPart = '00';
  else if (decPart.length === 1) decPart = decPart + '0';
  // 如果超过两位，已经通过 floatNumCut 截取到两位了
  return sign + intPart + '.' + decPart;
};

/**
 * 生成唯一id
 */
export const uuidv4 = () => {
  var d = Date.now();
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    d += performance.now(); // use high-precision timer if available
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

/**
 * 读取系统剪贴板文本
 */
export const readTextFromClipboard = async () => {
  const showUnavailableToast = () => {
    window.$toast({
      type: 'error',
      text: '粘贴权限不足，请长按输入栏粘贴',
    });
  };

  // 如果浏览器支持 clipboard，则读取粘贴内容
  try {
    if (navigator.clipboard && navigator.clipboard.readText) {
      const text = await navigator.clipboard.readText();
      if (text) {
        return text;
      }
    }
    showUnavailableToast();
    return '';
  } catch {
    showUnavailableToast();
    return '';
  }
};

/**
 *  复制功能
 */
export const copyToClipboard = text => {
  if (navigator.clipboard && window.isSecureContext) {
    // 优先用新 API
    return navigator.clipboard
      .writeText(text)
      .then(() => {
        window.$toast({ text: '复制成功', type: 'success' });
      })
      .catch(() => {
        // 降级处理
        fallbackCopy(text);
      });
  } else {
    // 兼容老浏览器
    fallbackCopy(text);
  }
};

function fallbackCopy(text) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  // textarea.focus();
  textarea.select();
  try {
    document.execCommand('copy');

    window.$toast('复制成功');
  } catch {
    window.$toast('复制失败');
  }
  document.body.removeChild(input);
}

/**
 * 千分位格式化
 */
export const formatNumberToThousand = num => {
  // 如果num为null、undefined或NaN，则返回0
  if (num === null || num === undefined || isNaN(num)) {
    return '0';
  }

  // 将num转换为字符串
  const numStr = num.toString();

  // 将numStr按小数点分割为整数部分和小数部分
  const parts = numStr.split('.');

  // 处理整数部分，使用更安全的方法
  let integerPart = parts[0];

  // 初始化一个空字符串，用于存储格式化后的整数部分
  let formattedInteger = '';

  // 从右往左每3位添加逗号
  for (let i = 0; i < integerPart.length; i++) {
    if (i > 0 && (integerPart.length - i) % 3 === 0) {
      formattedInteger += ',';
    }
    formattedInteger += integerPart[i];
  }

  // 如果有小数部分，保持原样
  return parts.length > 1 ? formattedInteger + '.' + parts[1] : formattedInteger;
};

/**
 * 获取设备ID
 * 从 localStorage 中读取设备ID，如果不存在则生成一个新的并存储
 */
export const getDeviceId = () => {
  try {
    const deviceIdKey = 'deviceId';
    let deviceId = localStorage.getItem(deviceIdKey);

    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem(deviceIdKey, deviceId);
    }

    return deviceId;
  } catch {
    return uuidv4();
  }
};

/**
 * 发送消息到原生
 */
export const sendMessageToNative = url => {
  var iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  document.body.appendChild(iframe);

  // 清除 iframe，避免内存泄漏
  setTimeout(function () {
    document.body.removeChild(iframe);
  }, 100);
};

/**
 * 验证手机号
 */
export const verifyPhoneNo = (phoneNo, phoneNoLength, hiddenToast) => {
  // 手机号为空
  if (!phoneNo) {
    if (!hiddenToast) {
      window.$toast({
        type: 'error',
        text: '请输入手机号码',
      });
    }
    return false;
  }

  // 手机号格式不正确
  if (phoneNoLength && phoneNo.length !== phoneNoLength) {
    if (!hiddenToast) {
      window.$toast({
        type: 'error',
        text: '手机号格式不正确',
      });
    }
    return false;
  }

  return true;
};

/**
 * session中验证码倒计结束时间戳 读写
 */
export const sessionCountdownEndTimeRC = (key, { phoneNo, countdownEndTime }) => {
  // 写入结束时间
  if (countdownEndTime) {
    // 设置为session
    sessionStorage.setItem(
      key,
      JSON.stringify([
        ...smsCodeCountdownEndTimeList,
        {
          phoneNo,
          smsCodeCountdownEndTime,
        },
      ])
    );
  } else {
    // 读取结束时间
    const smsLoginSession = sessionStorage.getItem(key);

    if (smsLoginSession) {
      const data = JSON.parse(smsLoginSession);

      for (const item of data) {
        if (item.phoneNo === phoneNo) {
          // 如果结束时间大于当前时间，则返回结束时间
          if (item.smsCodeCountdownEndTime > Date.now()) {
            return item.smsCodeCountdownEndTime;
          } else {
            // 清除数据
            sessionStorage.setItem(key, JSON.stringify(data.filter(item => item.phoneNo !== phoneNo)));
            return 0;
          }
        }
      }
    }
  }

  return 0;
};

/**
 * 号码中间取4位转换为*
 */
export const phoneNoMask = phoneNo => {
  const phoneNoArr = phoneNo.split(' ');
  if (phoneNoArr.length == 2) {
    return phoneNoArr[0] + ' ' + phoneNoArr[1].slice(0, 3) + '****' + phoneNoArr[1].slice(7);
  }
  return phoneNo;
};

/**
 * 验证地址
 */
export const addressVerify = (protocol, address) => {
  if (protocol === 'ERC20') {
    if (address.length !== 42 || address.slice(0, 2) !== '0x') {
      return false;
    }
  }
  if (protocol === 'TRC20') {
    if (address.length !== 34 || address.slice(0, 1) !== 'T') {
      return false;
    }
  }
  if (protocol === 'C') {
    const cCoinOk = (address.length === 34 && address.slice(0, 1) === 'C') || (address.length === 36 && address.slice(0, 2) === '68');
    if (!cCoinOk) {
      return false;
    }
  }
  return true;
};

/** 百度系（含 iOS 端 BIDUBrowser / baidubrowser，UA 中不一定含连续 baidu 子串） */
export const isBaiduFamilyBrowser = () => {
  try {
    const ua = navigator.userAgent || navigator.vendor || window.opera || '';
    return /Baidu|BIDUBrowser|baidubrowser|baidu|baiduboxapp/i.test(ua);
  } catch {
    return false;
  }
};

/** iOS 上的百度浏览器（教程视频需延迟赋 src 等规避内核异常） */
export const isIosBaiduBrowser = () => {
  try {
    return isBaiduFamilyBrowser() && getDeviceType() === 'iOS';
  } catch {
    return false;
  }
};

/**
 * 获取当前是否为不能外跳的浏览器
 */
export const isNotExternalJumpBrowser = () => {
  try {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (isBaiduFamilyBrowser()) {
      return true;
    }

    // 搜狗和QQ浏览器
    if (/MMQBrowser/.test(userAgent) || /SogouMobileBrowser/.test(userAgent) || /MQQBrowser/.test(userAgent)) {
      return true;
    }

    // uc浏览器（国内 UA 多为连续 UCBrowser；海外版常见「UC Browser」带空格；另有 UCWEB 等）
    if (/UC\s*Browser|UCWEB/i.test(userAgent)) {
      return true;
    }

    // 夸克浏览器
    if (/Quark/i.test(userAgent)) {
      return true;
    }
  } catch {
    return false;
  }

  return false;
};

/**
 * 收银台存款教程视频是否跳过提前挂载（仅百度系浏览器）
 */
export const shouldSkipCounterVideoPreload = () => isBaiduFamilyBrowser();

/**
 * 是否可通过 x-safari-https:// 等 Scheme 唤起系统 Safari（当前仅判断 iOS 18+）
 */
export const canOpenSafariViaScheme = () => {
  if (getDeviceType() !== 'iOS') return false;
  const ua = navigator.userAgent || '';
  const m = ua.match(/(?:CPU (?:iPhone |iPad )?OS|CPU OS|iPhone OS) (\d+)[_/]/);
  if (!m) return false;
  const major = parseInt(m[1], 10);
  return major >= 18;
};
